interface NumericCellProps {
  value: unknown;
  /**
   * Format pattern. Characters after the decimal point:
   *   '0' — required digit (always shown)
   *   '#' — optional digit (shown only if the value has one)
   *
   * Examples:
   *   '0'       → integer only (42)
   *   '0.00'    → always 2 decimals (42.00, 42.50)
   *   '0.##'    → 0–2 decimals, no trailing zeros (42, 42.5, 42.57)
   *   '0.0##'   → 1–3 decimals (42.0, 42.5, 42.567)
   *   undefined → backward-compat default: 2 decimals
   */
  format?: string;
  locale?: string;
}

/** Parse a format pattern into Intl.NumberFormat fraction-digit bounds. */
function decimalBounds(format?: string): { min: number; max: number } {
  if (format === undefined) return { min: 2, max: 2 };
  const dotIdx = format.indexOf('.');
  if (dotIdx === -1) return { min: 0, max: 0 };
  const tail = format.slice(dotIdx + 1);
  const required = [...tail].filter((c) => c === '0').length;
  return { min: required, max: tail.length };
}

export function NumericCell({ value, format, locale }: NumericCellProps) {
  if (value == null || value === '') return null;
  const num = Number(value);
  if (isNaN(num)) return <span>{String(value)}</span>;

  const { min, max } = decimalBounds(format);
  const formatted = new Intl.NumberFormat(locale, {
    minimumFractionDigits: min,
    maximumFractionDigits: max,
  }).format(num);
  return <span>{formatted}</span>;
}
